import React, { useState } from "react";
import TextField2T from "../textfields/TextField2T";
import MLTFGPT1 from "../textfields/MLTFGPT1";
import PrimaryButton from "../buttons/PrimaryButton";
import SecondaryButton from "../buttons/SecondaryButton";
import Icon1 from "../icons/Icon";
import EquipmentTypeSelect from "./EquipmentTypeSelect";

interface Equipment {
  brand?: string;
  model?: string;
  notes?: string;
  equipment_type?: string;
  name?: string;
  num_serie?: string;
  lifespan?: number;
  maintenance_tasks?: any[];
  customFields?: Record<string, any>;
}

interface AddEquipmentToBookshelfModalProps {
  onClose: () => void;
  onSubmit: (equipment: Equipment) => Promise<void>;
  loading?: boolean;
}


function AddEquipmentToBookshelfModal({
  onClose,
  onSubmit,
  loading = false,
}: AddEquipmentToBookshelfModalProps) {
  const [formData, setFormData] = useState<Equipment>({
    brand: "",
    model: "",
    notes: "",
    equipment_type: "",
    name: "",
    num_serie: "",
    lifespan: undefined,
    maintenance_tasks: [],
    customFields: {},
  });

  const handleInputChange = (field: keyof Equipment) => (e: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: e.target.value
    }));
  };

  const handleEquipmentTypeChange = (value: string) => {
    setFormData(prev => {
      const newData = {
        ...prev,
        equipment_type: value
      };

      // Reset "Unité" field if switching away from heat pump types
      if (value !== "Pompe à chaleur air-eau" && value !== "Pompe à chaleur air/air") {
        const { "Unité": _, ...remainingFields } = newData.customFields || {};
        newData.customFields = remainingFields;
      }

      return newData;
    });
  };

  const handleCustomFieldChange = (fieldName: string, value: any) => {
    setFormData(prev => {
      const newCustomFields = { ...(prev.customFields || {}) };

      // If value is empty, remove the field from customFields
      if (!value || value === "") {
        delete newCustomFields[fieldName];
      } else {
        newCustomFields[fieldName] = value;
      }

      return {
        ...prev,
        customFields: newCustomFields
      };
    });
  };

  const shouldShowExteriorUnitField =
    formData.equipment_type === "Pompe à chaleur air-eau" ||
    formData.equipment_type === "Pompe à chaleur air/air";

  const handleSubmit = async () => {
    if (!formData.name?.trim()) {
      alert("Le nom de l'équipement est requis");
      return;
    }

    await onSubmit(formData);
  };

  return (
    <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4 max-h-[90vh] overflow-y-auto">
      <div className="flex flex-row items-center justify-between mb-6">
        <div className="text-hx font-medium">Ajouter un équipement</div>
        <button
          onClick={onClose}
          className="p-1 hover:bg-gray-100 rounded-full"
          disabled={loading}
        >
          <Icon1 icon_name="close" height="20px" width="20px" />
        </button>
      </div>

      <div className="flex flex-col gap-4">
        <TextField2T
          label="Nom de l'équipement *"
          value={formData.name || ""}
          onChange={handleInputChange("name")}
          mandatory={true}
          disabled={loading}
        />

        <TextField2T
          label="Marque"
          value={formData.brand || ""}
          onChange={handleInputChange("brand")}
          disabled={loading}
        />

        <TextField2T
          label="Modèle"
          value={formData.model || ""}
          onChange={handleInputChange("model")}
          disabled={loading}
        />

        <EquipmentTypeSelect
          label="Type d'équipement"
          value={formData.equipment_type || ""}
          onChange={handleEquipmentTypeChange}
          disabled={loading}
        />

        {shouldShowExteriorUnitField && (
          <div className="relative">
            <label className="block text-sm text-gray-700 mb-1">
              Unité
            </label>
            <select
              value={formData.customFields?.["Unité"] || ""}
              onChange={(e) => handleCustomFieldChange("Unité", e.target.value)}
              disabled={loading}
              className="w-full h-11 px-2 rounded-xl border border-gray-200 bg-white focus:border-blue-300 focus:border-2 focus:outline-1 focus:outline-blue-100 disabled:bg-neutral-100 disabled:text-shadow_25"
              style={{ fontSize: "14px" }}
            >
              <option value="">Sélectionner...</option>
              <option value="interieur">Intérieure</option>
              <option value="exterieur">Extérieure</option>
            </select>
          </div>
        )}

        <TextField2T
          label="Numéro de série"
          value={formData.num_serie || ""}
          onChange={handleInputChange("num_serie")}
          disabled={loading}
        />

        <TextField2T
          label="Durée de vie (années)"
          type="number"
          value={formData.lifespan?.toString() || ""}
          onChange={(e) => {
            const value = e.target.value;
            setFormData(prev => ({
              ...prev,
              lifespan: value ? parseInt(value) : undefined
            }));
          }}
          disabled={loading}
        />

        <MLTFGPT1
          label="Notes"
          value={formData.notes || ""}
          onChange={handleInputChange("notes")}
          rows={3}
          disabled={loading}
        />

        <div className="flex flex-row gap-2 mt-4">
          <SecondaryButton 
            onClick={onClose}
            disabled={loading}
            className="flex-1"
          >
            Annuler
          </SecondaryButton>
          <PrimaryButton 
            onClick={handleSubmit}
            disabled={loading || !formData.name?.trim()}
            className="flex-1"
          >
            {loading ? (
              <div className="flex flex-row items-center gap-2">
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                Ajout...
              </div>
            ) : (
              "Ajouter"
            )}
          </PrimaryButton>
        </div>
      </div>
    </div>
  );
}

export default AddEquipmentToBookshelfModal;